import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { defaultFormulaConfig } from "../helpers/allowance_defaults"

export default class extends Controller {
  static targets = [
    "fieldsCanvas",
    "allowancesCanvas",
    "approvalsCanvas",
    "coreFieldsCanvas",
    "workingHoursCanvas",
    "fieldMenu",
    "allowanceMenu",
    "approvalMenu",
    "fieldsEmptyState",
    "allowancesEmptyState",
    "approvalsEmptyState",
    "workingHoursEmptyState",
    "groupContainer",
    "groupMenu",
  ]

  static values = {
    fieldsReorderUrl: String,
    allowancesReorderUrl: String,
    approvalsReorderUrl: String,
    coreFieldsReorderUrl: String,
    workingHoursReorderUrl: String,
    // i18n messages
    saveTemplateFirst: { type: String, default: "Please save the template first." },
    allowanceCreateError: { type: String, default: "Could not create allowance" },
    approvalCreateError: { type: String, default: "Could not create approval" },
    workingHourCreateError: {
      type: String,
      default: "Could not create working hour period",
    },
    newAllowanceFallback: { type: String, default: "New Allowance" },
    newApprovalFallback: { type: String, default: "New Approval Step" },
    newWorkingHourFallback: { type: String, default: "Neuer Zeitraum" },
  }

  connect() {
    this.initializeSortable()
    this.initializeGroupSortables()
    this.groupSortables = new Map()
    // Close menus when clicking outside
    this.boundHandleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.boundHandleOutsideClick)
  }

  // Safely destroy a Sortable instance, handling cases where the DOM element is already removed
  safeDestroySortable(sortable) {
    if (!sortable) return
    try {
      sortable.destroy()
    } catch (_e) {
      // Ignore errors when element is already removed from DOM
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundHandleOutsideClick)
    this.safeDestroySortable(this.fieldsSortable)
    this.safeDestroySortable(this.allowancesSortable)
    this.safeDestroySortable(this.coreFieldsSortable)
    this.safeDestroySortable(this.workingHoursSortable)
    // Destroy all group sortables
    if (this.groupSortables) {
      this.groupSortables.forEach((sortable) => this.safeDestroySortable(sortable))
    }
  }

  // Called when a new group container is connected (via Turbo)
  groupContainerTargetConnected(container) {
    this.initializeSortableOnGroup(container)
  }

  groupContainerTargetDisconnected(container) {
    const groupId = container.dataset.groupId
    if (this.groupSortables && this.groupSortables.has(groupId)) {
      this.safeDestroySortable(this.groupSortables.get(groupId))
      this.groupSortables.delete(groupId)
    }
  }

  handleOutsideClick(event) {
    // Close field menu if clicking outside
    if (
      this.hasFieldMenuTarget &&
      !this.fieldMenuTarget.contains(event.target) &&
      !event.target.closest('[data-action*="toggleFieldMenu"]')
    ) {
      this.fieldMenuTarget.classList.add("hidden")
    }
    // Close allowance menu if clicking outside
    if (
      this.hasAllowanceMenuTarget &&
      !this.allowanceMenuTarget.contains(event.target) &&
      !event.target.closest('[data-action*="toggleAllowanceMenu"]')
    ) {
      this.allowanceMenuTarget.classList.add("hidden")
    }
    // Close approval menu if clicking outside
    if (
      this.hasApprovalMenuTarget &&
      !this.approvalMenuTarget.contains(event.target) &&
      !event.target.closest('[data-action*="toggleApprovalMenu"]')
    ) {
      this.approvalMenuTarget.classList.add("hidden")
    }
    // Close all group menus if clicking outside
    this.groupMenuTargets.forEach((menu) => {
      if (
        !menu.contains(event.target) &&
        !event.target.closest('[data-action*="toggleGroupMenu"]')
      ) {
        menu.classList.add("hidden")
      }
    })
  }

  initializeSortable() {
    // Initialize sortable for core fields
    if (this.hasCoreFieldsCanvasTarget) {
      this.coreFieldsSortable = new Sortable(this.coreFieldsCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-blue-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleCoreFieldReorder(event),
      })
    }

    // Initialize sortable for fields
    if (this.hasFieldsCanvasTarget) {
      this.fieldsSortable = new Sortable(this.fieldsCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-green-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleFieldReorder(event),
      })
    }

    // Initialize sortable for allowances
    if (this.hasAllowancesCanvasTarget) {
      this.allowancesSortable = new Sortable(this.allowancesCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-green-50",
        dragClass: "shadow-lg",
        onMove: (event) => this.allowanceMoveAllowed(event),
        onEnd: (event) => this.handleAllowanceReorder(event),
      })
    }

    // Initialize sortable for working hours
    if (this.hasWorkingHoursCanvasTarget) {
      this.workingHoursSortable = new Sortable(this.workingHoursCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-blue-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleWorkingHourReorder(event),
      })
    }

    // Initialize sortable for approvals
    if (this.hasApprovalsCanvasTarget) {
      this.approvalsSortable = new Sortable(this.approvalsCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-blue-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleApprovalReorder(event),
      })
    }
  }

  // Toggle field type dropdown menu
  toggleFieldMenu(event) {
    event.stopPropagation()
    if (this.hasFieldMenuTarget) {
      this.fieldMenuTarget.classList.toggle("hidden")
    }
    // Close allowance menu if open
    if (this.hasAllowanceMenuTarget) {
      this.allowanceMenuTarget.classList.add("hidden")
    }
  }

  // Toggle allowance type dropdown menu
  toggleAllowanceMenu(event) {
    event.stopPropagation()
    if (this.hasAllowanceMenuTarget) {
      this.allowanceMenuTarget.classList.toggle("hidden")
    }
    // Close other menus if open
    if (this.hasFieldMenuTarget) {
      this.fieldMenuTarget.classList.add("hidden")
    }
    if (this.hasApprovalMenuTarget) {
      this.approvalMenuTarget.classList.add("hidden")
    }
  }

  // Toggle approval type dropdown menu
  toggleApprovalMenu(event) {
    event.stopPropagation()
    if (this.hasApprovalMenuTarget) {
      this.approvalMenuTarget.classList.toggle("hidden")
    }
    // Close other menus if open
    if (this.hasFieldMenuTarget) {
      this.fieldMenuTarget.classList.add("hidden")
    }
    if (this.hasAllowanceMenuTarget) {
      this.allowanceMenuTarget.classList.add("hidden")
    }
  }

  // Add a new field via AJAX
  addField(event) {
    event.preventDefault()
    const fieldType = event.currentTarget.dataset.fieldType
    this.createField(fieldType)
    if (this.hasFieldMenuTarget) {
      this.fieldMenuTarget.classList.add("hidden")
    }
  }

  // Add a new group via AJAX
  addGroup(event) {
    event.preventDefault()
    this.createField("group")
    if (this.hasFieldMenuTarget) {
      this.fieldMenuTarget.classList.add("hidden")
    }
  }

  // Toggle group's add field menu
  toggleGroupMenu(event) {
    event.stopPropagation()
    const menu = event.currentTarget
      .closest("[data-group-card]")
      ?.querySelector('[data-timesheet-template-builder-target="groupMenu"]')
    if (menu) {
      menu.classList.toggle("hidden")
    }
    // Close other menus
    this.groupMenuTargets.forEach((m) => {
      if (m !== menu) m.classList.add("hidden")
    })
  }

  // Add a field to a specific group
  addFieldToGroup(event) {
    event.preventDefault()
    const fieldType = event.currentTarget.dataset.fieldType
    const parentFieldId = event.currentTarget.dataset.parentFieldId
    this.createFieldInGroup(fieldType, parentFieldId)
    // Close the group menu
    const menu = event.currentTarget.closest('[data-timesheet-template-builder-target="groupMenu"]')
    if (menu) {
      menu.classList.add("hidden")
    }
  }

  // Add a new allowance via AJAX
  addAllowance(event) {
    event.preventDefault()
    const allowanceType = event.currentTarget.dataset.allowanceType
    const allowanceName = event.currentTarget.dataset.allowanceName
    this.createAllowance(allowanceType, allowanceName)
    if (this.hasAllowanceMenuTarget) {
      this.allowanceMenuTarget.classList.add("hidden")
    }
  }

  // Add a new approval via AJAX
  addApproval(event) {
    event.preventDefault()
    const approverType = event.currentTarget.dataset.approverType
    // Get the translated name from the button's data attribute
    const approvalName = event.currentTarget.dataset.approvalName
    this.createApproval(approverType, approvalName)
    if (this.hasApprovalMenuTarget) {
      this.approvalMenuTarget.classList.add("hidden")
    }
  }

  getTemplateId() {
    // Extract template ID from the reorder URL
    if (this.hasFieldsReorderUrlValue) {
      const match = this.fieldsReorderUrlValue.match(/timesheet_templates\/(\d+)/)
      if (match) return match[1]
    }
    return null
  }

  createField(fieldType) {
    const templateId = this.getTemplateId()
    if (!templateId) {
      this.showNotification("Bitte speichern Sie die Vorlage zuerst.", "error")
      return
    }

    const defaultLabels = {
      text: "Text",
      number: "Zahl",
      decimal: "Dezimalzahl",
      date: "Datum",
      time: "Uhrzeit",
      datetime: "Datum & Uhrzeit",
      dropdown: "Auswahl",
      multi_select: "Mehrfachauswahl",
      checkbox: "Checkbox",
      currency: "Währung",
      hours: "Stunden",
      duration: "Dauer",
      boq_position: "LV-Position",
      group: "Neue Gruppe",
    }

    const label = defaultLabels[fieldType] || fieldType.charAt(0).toUpperCase() + fieldType.slice(1)
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/timesheet_templates/${templateId}/timesheet_template_fields`

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({
        timesheet_template_field: {
          field_type: fieldType,
          label: label,
          placeholder: "",
        },
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Failed to create field")
        }
      })
      .then((html) => {
        // Process Turbo Stream response
        Turbo.renderStreamMessage(html)
        // Hide empty state if present
        if (this.hasFieldsEmptyStateTarget) {
          this.fieldsEmptyStateTarget.classList.add("hidden")
        }
        // Re-initialize sortable
        this.reinitializeFieldsSortable()
      })
      .catch((error) => {
        console.error("Error creating field:", error)
        this.showNotification("Feld konnte nicht erstellt werden", "error")
      })
  }

  createFieldInGroup(fieldType, parentFieldId) {
    const templateId = this.getTemplateId()
    if (!templateId) {
      this.showNotification("Bitte speichern Sie die Vorlage zuerst.", "error")
      return
    }

    const defaultLabels = {
      text: "Text",
      number: "Zahl",
      decimal: "Dezimalzahl",
      date: "Datum",
      time: "Uhrzeit",
      datetime: "Datum & Uhrzeit",
      dropdown: "Auswahl",
      multi_select: "Mehrfachauswahl",
      checkbox: "Checkbox",
      currency: "Währung",
      hours: "Stunden",
      duration: "Dauer",
    }

    const label = defaultLabels[fieldType] || fieldType.charAt(0).toUpperCase() + fieldType.slice(1)
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/timesheet_templates/${templateId}/timesheet_template_fields`

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({
        timesheet_template_field: {
          field_type: fieldType,
          label: label,
          placeholder: "",
          parent_field_id: parentFieldId,
        },
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Failed to create field")
        }
      })
      .then((html) => {
        // Process Turbo Stream response
        Turbo.renderStreamMessage(html)
      })
      .catch((error) => {
        console.error("Error creating field in group:", error)
        this.showNotification("Feld konnte nicht erstellt werden", "error")
      })
  }

  // Initialize sortable on all group containers
  initializeGroupSortables() {
    this.groupContainerTargets.forEach((container) => {
      this.initializeSortableOnGroup(container)
    })
  }

  // Initialize sortable on a specific group container
  initializeSortableOnGroup(container) {
    const groupId = container.dataset.groupId
    if (!groupId) return

    // Destroy existing sortable if present
    if (this.groupSortables && this.groupSortables.has(groupId)) {
      this.safeDestroySortable(this.groupSortables.get(groupId))
    }

    const sortable = new Sortable(container, {
      animation: 150,
      handle: ".drag-handle",
      ghostClass: "opacity-50",
      chosenClass: "bg-green-50",
      dragClass: "shadow-lg",
      onEnd: (event) => this.handleGroupReorder(event, groupId),
    })

    if (!this.groupSortables) {
      this.groupSortables = new Map()
    }
    this.groupSortables.set(groupId, sortable)
  }

  // Handle reordering within a group
  handleGroupReorder(event, groupId) {
    if (!this.hasFieldsReorderUrlValue) return

    const container = event.target
    const items = Array.from(container.children)
    const positions = items.map((item, index) => ({
      id: item.id.replace("timesheet_template_field_", ""),
      position: index + 1,
    }))

    this.sendReorderRequest(this.fieldsReorderUrlValue, { positions, parent_field_id: groupId })
  }

  createAllowance(typeKey, allowanceName = null) {
    const templateId = this.getTemplateId()
    if (!templateId) {
      this.showNotification(this.saveTemplateFirstValue, "error")
      return
    }

    const name = allowanceName || this.newAllowanceFallbackValue
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/timesheet_templates/${templateId}/timesheet_template_allowances`

    const requestBody = {
      timesheet_template_allowance: {
        name: name,
        allowance_type: typeKey,
        formula_config: defaultFormulaConfig(typeKey),
      },
    }

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Failed to create allowance")
        }
      })
      .then((html) => {
        // Process Turbo Stream response
        Turbo.renderStreamMessage(html)
        // Hide empty state if present
        if (this.hasAllowancesEmptyStateTarget) {
          this.allowancesEmptyStateTarget.classList.add("hidden")
        }
        // Re-initialize sortable
        this.reinitializeAllowancesSortable()
      })
      .catch((error) => {
        console.error("Error creating allowance:", error)
        this.showNotification(this.allowanceCreateErrorValue, "error")
      })
  }

  createApproval(approverType, approvalName = null) {
    const templateId = this.getTemplateId()
    if (!templateId) {
      this.showNotification(this.saveTemplateFirstValue, "error")
      return
    }

    // Use provided name or fallback
    const name = approvalName || this.newApprovalFallbackValue
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/timesheet_templates/${templateId}/timesheet_template_approvals`

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      body: JSON.stringify({
        timesheet_template_approval: {
          name: name,
          approver_type: approverType,
          required: true,
        },
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error("Failed to create approval step")
        }
      })
      .then((html) => {
        // Process Turbo Stream response
        Turbo.renderStreamMessage(html)
        // Hide empty state if present
        if (this.hasApprovalsEmptyStateTarget) {
          this.approvalsEmptyStateTarget.classList.add("hidden")
        }
        // Show canvas if hidden
        if (this.hasApprovalsCanvasTarget) {
          this.approvalsCanvasTarget.classList.remove("hidden")
        }
        // Re-initialize sortable
        this.reinitializeApprovalsSortable()
      })
      .catch((error) => {
        console.error("Error creating approval:", error)
        this.showNotification(this.approvalCreateErrorValue, "error")
      })
  }

  // Calculate day-of-year for a given month/day using 2024 (leap year) as reference
  ydayFor(month, day) {
    // Mirroring Ruby: Date.new(2024, month, capped_day).yday
    const daysInMonth = new Date(2024, month, 0).getDate()
    const cappedDay = Math.min(day, daysInMonth)
    const date = new Date(2024, month - 1, cappedDay)
    const start = new Date(2024, 0, 1)
    return Math.floor((date - start) / 86400000) + 1
  }

  // Convert day-of-year back to { month, day } using 2024 as reference
  dateFromYday(yday) {
    const date = new Date(2024, 0, yday)
    return { month: date.getMonth() + 1, day: date.getDate() }
  }

  // Find the next available date range gap among existing working hour periods
  findNextAvailableRange() {
    if (!this.hasWorkingHoursCanvasTarget) return null

    const cards = this.workingHoursCanvasTarget.querySelectorAll("[data-start-month]")
    if (cards.length === 0) return null

    // Collect all covered days (as a Set for fast lookup)
    const coveredDays = new Set()
    cards.forEach((card) => {
      const startMonth = parseInt(card.dataset.startMonth, 10)
      const startDay = parseInt(card.dataset.startDay, 10)
      const endMonth = parseInt(card.dataset.endMonth, 10)
      const endDay = parseInt(card.dataset.endDay, 10)

      const startYday = this.ydayFor(startMonth, startDay)
      const endYday = this.ydayFor(endMonth, endDay)

      if (startYday <= endYday) {
        for (let d = startYday; d <= endYday; d++) coveredDays.add(d)
      } else {
        for (let d = startYday; d <= 366; d++) coveredDays.add(d)
        for (let d = 1; d <= endYday; d++) coveredDays.add(d)
      }
    })

    // Find the first uncovered day
    let gapStart = null
    for (let d = 1; d <= 366; d++) {
      if (!coveredDays.has(d)) {
        gapStart = d
        break
      }
    }

    if (gapStart === null) return null // Full year covered

    // Find the end of the contiguous gap
    let gapEnd = gapStart
    for (let d = gapStart + 1; d <= 366; d++) {
      if (coveredDays.has(d)) break
      gapEnd = d
    }

    // If the gap wraps around the year end, extend into the beginning
    if (gapEnd === 366 && !coveredDays.has(1)) {
      for (let d = 1; d < gapStart; d++) {
        if (coveredDays.has(d)) break
        gapEnd = d
      }
    }

    const startDate = this.dateFromYday(gapStart)
    const endDate = this.dateFromYday(gapEnd)
    return {
      start_month: startDate.month,
      start_day: startDate.day,
      end_month: endDate.month,
      end_day: endDate.day,
    }
  }

  // Add a new working hour period via AJAX
  addWorkingHour(event) {
    event.preventDefault()
    this.createWorkingHour()
  }

  createWorkingHour() {
    const templateId = this.getTemplateId()
    if (!templateId) {
      this.showNotification(this.saveTemplateFirstValue, "error")
      return
    }

    // Find the next available date range gap, or fallback to full year
    const range = this.findNextAvailableRange() || {
      start_month: 1,
      start_day: 1,
      end_month: 12,
      end_day: 31,
    }

    const name = this.newWorkingHourFallbackValue
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const endpoint = `/settings/timesheet_templates/${templateId}/timesheet_template_working_hours`

    const dayDetails = {}
    const weekdays = ["monday", "tuesday", "wednesday", "thursday", "friday"]
    weekdays.forEach((day) => {
      dayDetails[day] = { time_from: "07:00", time_to: "16:00", time_break: "00:30" }
    })
    dayDetails["saturday"] = { time_from: "", time_to: "", time_break: "" }
    dayDetails["sunday"] = { time_from: "", time_to: "", time_break: "" }

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html, application/json",
      },
      body: JSON.stringify({
        timesheet_template_working_hour: {
          name: name,
          start_month: range.start_month,
          start_day: range.start_day,
          end_month: range.end_month,
          end_day: range.end_day,
          day_details: dayDetails,
        },
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.text().then((html) => {
            Turbo.renderStreamMessage(html)
            if (this.hasWorkingHoursEmptyStateTarget) {
              this.workingHoursEmptyStateTarget.classList.add("hidden")
            }
            // The canvas is rendered hidden in the empty state; reveal it so the
            // first appended card is visible without a page refresh.
            if (this.hasWorkingHoursCanvasTarget) {
              this.workingHoursCanvasTarget.classList.remove("hidden")
            }
            this.reinitializeWorkingHoursSortable()
          })
        } else {
          return response.json().then((data) => {
            const message = (data.errors || []).join(", ") || this.workingHourCreateErrorValue
            this.showNotification(message, "error")
          })
        }
      })
      .catch((error) => {
        console.error("Error creating working hour period:", error)
        this.showNotification(this.workingHourCreateErrorValue, "error")
      })
  }

  reinitializeWorkingHoursSortable() {
    this.safeDestroySortable(this.workingHoursSortable)
    if (this.hasWorkingHoursCanvasTarget) {
      this.workingHoursSortable = new Sortable(this.workingHoursCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-blue-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleWorkingHourReorder(event),
      })
    }
  }

  handleWorkingHourReorder(_event) {
    if (!this.hasWorkingHoursReorderUrlValue) return

    const items = Array.from(this.workingHoursCanvasTarget.children)
    const positions = items.map((item, index) => ({
      id: item.id.replace("timesheet_template_working_hour_", ""),
      position: index + 1,
    }))

    this.sendReorderRequest(this.workingHoursReorderUrlValue, { positions })
  }

  reinitializeFieldsSortable() {
    this.safeDestroySortable(this.fieldsSortable)
    if (this.hasFieldsCanvasTarget) {
      this.fieldsSortable = new Sortable(this.fieldsCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-green-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleFieldReorder(event),
      })
    }
  }

  reinitializeAllowancesSortable() {
    this.safeDestroySortable(this.allowancesSortable)
    if (this.hasAllowancesCanvasTarget) {
      this.allowancesSortable = new Sortable(this.allowancesCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-green-50",
        dragClass: "shadow-lg",
        onMove: (event) => this.allowanceMoveAllowed(event),
        onEnd: (event) => this.handleAllowanceReorder(event),
      })
    }
  }

  reinitializeApprovalsSortable() {
    this.safeDestroySortable(this.approvalsSortable)
    if (this.hasApprovalsCanvasTarget) {
      this.approvalsSortable = new Sortable(this.approvalsCanvasTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        chosenClass: "bg-blue-50",
        dragClass: "shadow-lg",
        onEnd: (event) => this.handleApprovalReorder(event),
      })
    }
  }

  handleCoreFieldReorder(_event) {
    if (!this.hasCoreFieldsReorderUrlValue) return

    const items = Array.from(this.coreFieldsCanvasTarget.children)
    const positions = items.map((item, index) => ({
      field_key: item.dataset.fieldKey,
      position: index + 1,
    }))

    this.sendReorderRequest(this.coreFieldsReorderUrlValue, { positions })
  }

  handleFieldReorder(_event) {
    if (!this.hasFieldsReorderUrlValue) return

    const items = Array.from(this.fieldsCanvasTarget.children)
    const positions = items.map((item, index) => ({
      id: item.id.replace("timesheet_template_field_", ""),
      position: index + 1,
    }))

    this.sendReorderRequest(this.fieldsReorderUrlValue, { positions })
  }

  // Zulagen-Profil copies stay first, in the profile's order (data-profile-copy).
  allowanceMoveAllowed(event) {
    return !event.related.dataset.profileCopy
  }

  handleAllowanceReorder(_event) {
    if (!this.hasAllowancesReorderUrlValue) return

    const items = Array.from(this.allowancesCanvasTarget.children)
    const positions = items.map((item, index) => ({
      id: item.id.replace("timesheet_template_allowance_", ""),
      position: index + 1,
    }))

    this.sendReorderRequest(this.allowancesReorderUrlValue, { positions })
  }

  handleApprovalReorder(_event) {
    if (!this.hasApprovalsReorderUrlValue) return

    const items = Array.from(this.approvalsCanvasTarget.children)
    const positions = items.map((item, index) => ({
      id: item.id.replace("timesheet_template_approval_", ""),
      position: index + 1,
    }))

    this.sendReorderRequest(this.approvalsReorderUrlValue, { positions })
  }

  sendReorderRequest(url, data) {
    const csrfToken = document.querySelector("[name='csrf-token']")?.content

    fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Reorder failed")
        }
      })
      .catch((error) => {
        console.error("Error reordering:", error)
      })
  }

  // Update formula config fields based on selected formula type
  updateFormulaConfig(event) {
    const formulaType = event.target.value
    const container = event.target.closest("form")?.querySelector("[data-formula-config-container]")
    if (!container) return

    // Hide all formula config sections
    container.querySelectorAll("[data-formula-type]").forEach((section) => {
      section.classList.add("hidden")
    })

    // Show the relevant section
    const relevantSection = container.querySelector(`[data-formula-type="${formulaType}"]`)
    if (relevantSection) {
      relevantSection.classList.remove("hidden")
    }
  }

  // Update field settings based on selected field type
  updateFieldSettings(event) {
    const fieldType = event.target.value
    const form = event.target.closest("form")
    const optionsSection = form?.querySelector("[data-field-options]")
    const validationSection = form?.querySelector("[data-field-validation]")
    const inputStyleSection = form?.querySelector("[data-field-input-style]")

    // Show options section for dropdown/multi_select
    if (optionsSection) {
      if (["dropdown", "multi_select"].includes(fieldType)) {
        optionsSection.classList.remove("hidden")
      } else {
        optionsSection.classList.add("hidden")
      }
    }

    // Show validation section for number/decimal/currency
    if (validationSection) {
      if (["number", "decimal", "currency", "hours", "duration"].includes(fieldType)) {
        validationSection.classList.remove("hidden")
      } else {
        validationSection.classList.add("hidden")
      }
    }

    // Show input style section for text fields
    if (inputStyleSection) {
      if (fieldType === "text") {
        inputStyleSection.classList.remove("hidden")
      } else {
        inputStyleSection.classList.add("hidden")
      }
    }

    // Per-entry option is not available for group/boq_position fields
    const perEntrySection = form?.querySelector("[data-field-per-entry]")
    if (perEntrySection) {
      if (["group", "boq_position"].includes(fieldType)) {
        perEntrySection.classList.add("hidden")
      } else {
        perEntrySection.classList.remove("hidden")
      }
    }

    // "Bei Check-in änderbar" only applies to the option-based types and text —
    // the value doubles as the key the day's check-ins are grouped by. Mirrors
    // TimesheetTemplateField::CHECK_IN_CHANGEABLE_FIELD_TYPES.
    const checkInChangeableSection = form?.querySelector("[data-field-check-in-changeable]")
    if (checkInChangeableSection) {
      if (["dropdown", "multi_select", "checkbox", "text"].includes(fieldType)) {
        checkInChangeableSection.classList.remove("hidden")
      } else {
        checkInChangeableSection.classList.add("hidden")
      }
    }
  }

  // Add option row for dropdown fields
  addOption(event) {
    event.preventDefault()
    const optionsList = event.target.closest("form")?.querySelector("[data-options-list]")
    if (!optionsList) return

    const index = optionsList.children.length
    const newRow = document.createElement("div")
    newRow.className = "flex items-center space-x-2 mt-2"
    newRow.innerHTML = `
      <input type="text"
             name="timesheet_template_field[settings][options][${index}][label]"
             placeholder="Bezeichnung"
             class="flex-1 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <input type="text"
             name="timesheet_template_field[settings][options][${index}][value]"
             placeholder="Wert (optional)"
             class="w-32 shadow-xs sm:text-sm border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500">
      <label class="inline-flex items-center" title="Standardwert">
        <input type="hidden" name="timesheet_template_field[settings][options][${index}][default]" value="0">
        <input type="checkbox"
               name="timesheet_template_field[settings][options][${index}][default]"
               value="1"
               class="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500">
      </label>
      <button type="button"
              class="p-1 text-gray-400 hover:text-red-500"
              data-action="click->timesheet-template-builder#removeOption">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `
    optionsList.appendChild(newRow)
    newRow.querySelector("input").focus()
  }

  removeOption(event) {
    event.preventDefault()
    const row = event.target.closest(".flex")
    if (row) {
      row.remove()
    }
  }

  showNotification(message, type = "info") {
    // Use existing notification system if available, otherwise fallback to alert
    if (window.Turbo && typeof window.Turbo.toast === "function") {
      window.Turbo.toast(message, type)
    } else {
      alert(message)
    }
  }
}
